/**
 * RDM Live Sync Hook
 *
 * Connects the Creately app to a local RDM viewer server for live updates.
 *
 * Usage:
 * 1. Start the RDM viewer server: `bun run scripts/rdm-viewer.ts family.rdm`
 * 2. Open Creately with: `http://localhost:5173/?rdm-sync=true`
 * 3. Edit the RDM file - changes appear in real-time
 *
 * Query params:
 * - ?rdm-sync=true - Connect to default ws://localhost:3333
 * - ?rdm-sync=ws://localhost:4444 - Connect to custom WebSocket URL
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import type { Scene } from '../core/models/scene';
import { Logger } from '../utils/logger';
import { toast } from 'sonner';

const logger = Logger.getLoggerWithPrefix('RdmSync');

const DEFAULT_WS_URL = 'ws://localhost:3333';
const RECONNECT_DELAY = 2000;
const MAX_RECONNECT_ATTEMPTS = 10;

interface RdmSyncMessage {
  type: 'init' | 'update' | 'error' | 'ping';
  scene?: Scene;
  filename?: string;
  error?: string;
}

export function useRdmSync() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadScene = useDiagramStore(state => state.loadScene);

  // Get WebSocket URL from query params
  const getWsUrl = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const rdmSync = params.get('rdm-sync');

    if (!rdmSync) return null;

    // ?rdm-sync=true uses default URL
    if (rdmSync === 'true' || rdmSync === '1') {
      return DEFAULT_WS_URL;
    }

    // ?rdm-sync=ws://... uses custom URL
    if (rdmSync.startsWith('ws://') || rdmSync.startsWith('wss://')) {
      return rdmSync;
    }

    return null;
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: RdmSyncMessage = JSON.parse(event.data);

      switch (data.type) {
        case 'init':
          logger.info('RDM sync initialized', { filename: data.filename });
          if (data.scene) {
            loadScene(data.scene);
            toast.success(`Loaded: ${data.filename || 'diagram'}`);
          }
          break;

        case 'update':
          logger.info('RDM update received', { filename: data.filename });
          if (data.scene) {
            loadScene(data.scene);
          }
          break;

        case 'error':
          logger.error('RDM sync error', { error: data.error });
          toast.error(`RDM Error: ${data.error}`);
          break;

        case 'ping':
          // Keep-alive, no action needed
          break;

        default:
          logger.warn('Unknown RDM sync message type', { data });
      }
    } catch (err) {
      logger.error('Failed to parse RDM sync message', err);
    }
  }, [loadScene]);

  const connect = useCallback((wsUrl: string) => {
    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    logger.info('Connecting to RDM sync server', { url: wsUrl });

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      logger.info('RDM sync connected');
      reconnectAttemptsRef.current = 0;
      toast.success('RDM Sync connected', {
        description: 'Edit your RDM file to see live updates',
        duration: 3000,
      });
    };

    ws.onmessage = handleMessage;

    ws.onerror = (error) => {
      logger.error('RDM sync WebSocket error', error);
    };

    ws.onclose = (event) => {
      logger.info('RDM sync disconnected', { code: event.code, reason: event.reason });
      wsRef.current = null;

      // Attempt to reconnect
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++;
        const delay = RECONNECT_DELAY * Math.min(reconnectAttemptsRef.current, 5);

        logger.info(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          const url = getWsUrl();
          if (url) {
            connect(url);
          }
        }, delay);
      } else {
        toast.error('RDM Sync disconnected', {
          description: 'Max reconnection attempts reached. Refresh to retry.',
        });
      }
    };
  }, [handleMessage, getWsUrl]);

  useEffect(() => {
    const wsUrl = getWsUrl();

    if (!wsUrl) {
      return; // RDM sync not enabled
    }

    // Add visual indicator that RDM sync is active
    if (typeof document !== 'undefined') {
      document.title = '[RDM Sync] ' + document.title.replace('[RDM Sync] ', '');
    }

    connect(wsUrl);

    return () => {
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [getWsUrl, connect]);

  // Return connection status for UI if needed
  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };
}
